import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Card, Form, Input, Button, message, Space, Select, Row, Col, Divider, Slider } from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    HolderOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    SoundOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';
import { playScannerTone } from './Scanner/scannerAudio';
import {
    SCANNER_VOLUME_MAX,
    SCANNER_VOLUME_MIN,
    getScannerVolumeMultiplier,
    setScannerVolumeMultiplier,
} from './Scanner/scannerVolume';

const FONT_STYLE_OPTIONS = [
    { value: 'normal', label: 'Обычный' },
    { value: 'bold', label: 'Жирный' },
    { value: 'italic', label: 'Курсив' },
    { value: 'bold-italic', label: 'Жирный курсив' },
];

const Profile = () => {
    const { user, logout } = useAuth();
    const [ozonForm] = Form.useForm();
    const [openRouterForm] = Form.useForm();
    const [nanoGptForm] = Form.useForm();
    const [displayStylesForm] = Form.useForm();
    const [nameReplacementsForm] = Form.useForm();
    const [ozonLoading, setOzonLoading] = useState(false);
    const [openRouterLoading, setOpenRouterLoading] = useState(false);
    const [nanoGptLoading, setNanoGptLoading] = useState(false);
    const [displayStylesLoading, setDisplayStylesLoading] = useState(false);
    const [nameReplacementsLoading, setNameReplacementsLoading] = useState(false);
    const [dragFromIndex, setDragFromIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [productTypes, setProductTypes] = useState([]);
    const [newTypeName, setNewTypeName] = useState('');
    const [editingTypeIndex, setEditingTypeIndex] = useState(null);
    const [editingTypeValue, setEditingTypeValue] = useState('');
    const [scannerVolume, setScannerVolume] = useState(getScannerVolumeMultiplier);

    useEffect(() => {
        const fetchApiSettings = async () => {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            try {
                const response = await axios.get(`${API_URL}/v1/Settings/getOzoneApi`, { headers });
                if (response.data) {
                    ozonForm.setFieldsValue(response.data);
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error('Error fetching Ozone API settings:', error);
                }
            }

            try {
                const response = await axios.get(`${API_URL}/v1/Settings/getOpenRouterApi`, { headers });
                if (response.data) {
                    openRouterForm.setFieldsValue(response.data);
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error('Error fetching OpenRouter API settings:', error);
                }
            }

            try {
                const response = await axios.get(`${API_URL}/v1/Settings/getNanoGptApi`, { headers });
                if (response.data) {
                    nanoGptForm.setFieldsValue(response.data);
                }
            } catch (error) {
                if (error.response?.status !== 404) {
                    console.error('Error fetching NanoGPT API settings:', error);
                }
            }

            try {
                const response = await axios.get(
                    `${API_URL}/v1/Settings/getProductDisplayStyles`,
                    { headers }
                );
                const rules = (response.data?.rules || []).map((rule) => ({
                    label: rule.label || rule.article || '',
                    article: rule.article || '',
                    backgroundColor: rule.backgroundColor || '#ffffff',
                    textColor: rule.textColor || '#000000',
                    fontStyle: rule.fontStyle || 'normal',
                }));
                let types = Array.isArray(response.data?.types)
                    ? response.data.types.map((item) => String(item || '').trim()).filter(Boolean)
                    : [];
                if (!types.length) {
                    const seen = new Set();
                    rules.forEach((rule) => {
                        const label = String(rule.label || '').trim();
                        if (label && !seen.has(label.toLowerCase())) {
                            seen.add(label.toLowerCase());
                            types.push(label);
                        }
                    });
                }
                setProductTypes(types);
                displayStylesForm.setFieldsValue({ rules });
            } catch (error) {
                console.error('Error fetching product display styles:', error);
                setProductTypes([]);
                displayStylesForm.setFieldsValue({ rules: [] });
            }

            try {
                const response = await axios.get(
                    `${API_URL}/v1/Settings/getProductNameReplacements`,
                    { headers }
                );
                const items = (response.data?.items || []).map((item) => ({
                    find: item.find || '',
                    replace: item.replace || '',
                }));
                nameReplacementsForm.setFieldsValue({
                    items: items.length
                        ? items
                        : [{ find: '', replace: '' }],
                });
            } catch (error) {
                console.error('Error fetching product name replacements:', error);
                nameReplacementsForm.setFieldsValue({
                    items: [{ find: '', replace: '' }],
                });
            }
        };

        fetchApiSettings();
    }, [ozonForm, openRouterForm, nanoGptForm, displayStylesForm, nameReplacementsForm]);

    if (!user) {
        return <Navigate to="/login" />;
    }

    const handleApiSubmit = async (values) => {
        try {
            setOzonLoading(true);
            const token = localStorage.getItem('token');
            const apiData = {
                ...values,
                username: user.username,
                type: 'OzoneApi'
            };

            await axios.post(`${API_URL}/v1/Settings/addOzoneApi`, apiData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            message.success('API настройки OZON успешно сохранены');
        } catch (error) {
            console.error('Error saving Ozone API settings:', error);
            message.error('Ошибка при сохранении API настроек OZON');
        } finally {
            setOzonLoading(false);
        }
    };

    const handleNanoGptSubmit = async (values) => {
        try {
            setNanoGptLoading(true);
            const token = localStorage.getItem('token');
            const apiData = {
                ...values,
                type: 'NanoGptApi',
            };

            await axios.post(`${API_URL}/v1/Settings/addNanoGptApi`, apiData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            message.success('Настройки NanoGPT успешно сохранены');
        } catch (error) {
            console.error('Error saving NanoGPT API settings:', error);
            message.error('Ошибка при сохранении настроек NanoGPT');
        } finally {
            setNanoGptLoading(false);
        }
    };

    const handleOpenRouterSubmit = async (values) => {
        try {
            setOpenRouterLoading(true);
            const token = localStorage.getItem('token');
            const apiData = {
                ...values,
                type: 'OpenRouterApi'
            };

            await axios.post(`${API_URL}/v1/Settings/addOpenRouterApi`, apiData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            message.success('Настройки GPT OpenRouter успешно сохранены');
        } catch (error) {
            console.error('Error saving OpenRouter API settings:', error);
            message.error('Ошибка при сохранении настроек OpenRouter');
        } finally {
            setOpenRouterLoading(false);
        }
    };

    const renameTypeInRules = (oldName, newName) => {
        const rules = displayStylesForm.getFieldValue('rules') || [];
        displayStylesForm.setFieldsValue({
            rules: rules.map((rule) =>
                rule?.label === oldName ? { ...rule, label: newName } : rule
            ),
        });
    };

    const clearTypeInRules = (typeName) => {
        const rules = displayStylesForm.getFieldValue('rules') || [];
        displayStylesForm.setFieldsValue({
            rules: rules.map((rule) =>
                rule?.label === typeName ? { ...rule, label: undefined } : rule
            ),
        });
    };

    const addProductType = () => {
        const name = newTypeName.trim();
        if (!name) {
            message.warning('Введите название типа');
            return;
        }
        if (productTypes.some((item) => item.toLowerCase() === name.toLowerCase())) {
            message.warning('Такой тип уже есть');
            return;
        }
        setProductTypes((prev) => [...prev, name]);
        setNewTypeName('');
    };

    const startEditProductType = (index) => {
        setEditingTypeIndex(index);
        setEditingTypeValue(productTypes[index] || '');
    };

    const cancelEditProductType = () => {
        setEditingTypeIndex(null);
        setEditingTypeValue('');
    };

    const saveEditProductType = () => {
        const nextName = editingTypeValue.trim();
        if (editingTypeIndex === null) return;
        if (!nextName) {
            message.warning('Введите название типа');
            return;
        }
        const oldName = productTypes[editingTypeIndex];
        if (
            productTypes.some(
                (item, index) =>
                    index !== editingTypeIndex && item.toLowerCase() === nextName.toLowerCase()
            )
        ) {
            message.warning('Такой тип уже есть');
            return;
        }
        setProductTypes((prev) =>
            prev.map((item, index) => (index === editingTypeIndex ? nextName : item))
        );
        if (oldName !== nextName) {
            renameTypeInRules(oldName, nextName);
        }
        cancelEditProductType();
    };

    const removeProductType = (index) => {
        const typeName = productTypes[index];
        setProductTypes((prev) => prev.filter((_, i) => i !== index));
        clearTypeInRules(typeName);
        if (editingTypeIndex === index) {
            cancelEditProductType();
        }
    };

    const handleDisplayStylesSubmit = async (values) => {
        try {
            setDisplayStylesLoading(true);
            const token = localStorage.getItem('token');
            const rules = (values.rules || [])
                .map((rule) => ({
                    label: (rule.label || '').trim(),
                    article: (rule.article || '').trim(),
                    backgroundColor: rule.backgroundColor || '#ffffff',
                    textColor: rule.textColor || '#000000',
                    fontStyle: rule.fontStyle || 'normal',
                }))
                .filter((rule) => rule.article && rule.label);

            await axios.post(
                `${API_URL}/v1/Settings/addProductDisplayStyles`,
                {
                    types: productTypes,
                    rules,
                    type: 'ProductDisplayStyles',
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Типы товаров сохранены');
        } catch (error) {
            console.error('Error saving product display styles:', error);
            message.error('Ошибка при сохранении типов товаров');
        } finally {
            setDisplayStylesLoading(false);
        }
    };

    const handleNameReplacementsSubmit = async (values) => {
        try {
            setNameReplacementsLoading(true);
            const token = localStorage.getItem('token');
            const items = (values.items || [])
                .map((item) => ({
                    find: (item.find || '').trim(),
                    replace: (item.replace || '').trim(),
                }))
                .filter((item) => item.find);

            await axios.post(
                `${API_URL}/v1/Settings/addProductNameReplacements`,
                {
                    items,
                    type: 'ProductNameReplacements',
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            message.success('Сокращения названий сохранены');
        } catch (error) {
            console.error('Error saving product name replacements:', error);
            message.error('Ошибка при сохранении сокращений названий');
        } finally {
            setNameReplacementsLoading(false);
        }
    };

    const typeSelectOptions = productTypes.map((typeName) => ({
        value: typeName,
        label: typeName,
    }));

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl space-y-6">
                <Card className="shadow-lg rounded-lg">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="mb-1 text-2xl font-bold text-gray-900">
                                Профиль пользователя
                            </h2>
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-gray-600">
                                <span>
                                    <span className="font-semibold">Имя:</span> {user.username}
                                </span>
                                <span>
                                    <span className="font-semibold">Email:</span> {user.email}
                                </span>
                                <span>
                                    <span className="font-semibold">Роль:</span>{' '}
                                    {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {user.role === 'admin' && (
                                <Link
                                    to="/admin"
                                    className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                                >
                                    Панель администратора
                                </Link>
                            )}
                            <Button danger type="primary" onClick={logout}>
                                Выйти
                            </Button>
                        </div>
                    </div>
                </Card>

                <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Браузер
                    </h3>
                    <Card title="Громкость звука сканера" className="rounded-lg shadow-lg">
                        <p className="mb-4 text-sm text-gray-500">
                            Сохраняется в этом браузере. Минимум — текущая громкость, максимум — в 30 раз громче.
                        </p>
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <Slider
                                className="min-w-0 flex-1"
                                min={SCANNER_VOLUME_MIN}
                                max={SCANNER_VOLUME_MAX}
                                step={0.1}
                                value={scannerVolume}
                                tooltip={{ formatter: (value) => `×${Number(value).toFixed(1)}` }}
                                marks={{
                                    1: 'Сейчас',
                                    30: '×30',
                                }}
                                onChange={(value) => {
                                    setScannerVolume(setScannerVolumeMultiplier(value));
                                }}
                            />
                            <Button
                                icon={<SoundOutlined />}
                                onClick={() => playScannerTone('success')}
                            >
                                Проверить
                            </Button>
                        </div>
                    </Card>
                </div>

                <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Интеграции и ключи API
                    </h3>
                    <Row gutter={[16, 16]} align="stretch">
                        <Col xs={24} lg={12} style={{ display: 'flex' }}>
                            <Card
                                title="Настройки API OZON"
                                className="rounded-lg shadow-lg"
                                style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                                styles={{
                                    body: {
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                }}
                            >
                                <Form
                                    form={ozonForm}
                                    layout="vertical"
                                    onFinish={handleApiSubmit}
                                    requiredMark={false}
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Form.Item
                                        name="name"
                                        label="Название"
                                        rules={[{ required: true, message: 'Введите название' }]}
                                    >
                                        <Input placeholder="Название для API ключа" />
                                    </Form.Item>

                                    <Form.Item
                                        name="client_id"
                                        label="Client ID"
                                        rules={[{ required: true, message: 'Введите Client ID' }]}
                                    >
                                        <Input placeholder="Client ID OZON" />
                                    </Form.Item>

                                    <Form.Item
                                        name="api_key"
                                        label="API Ключ"
                                        rules={[{ required: true, message: 'Введите API ключ' }]}
                                    >
                                        <Input.Password placeholder="API ключ OZON" />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0, marginTop: 'auto' }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={ozonLoading}
                                            block
                                        >
                                            Сохранить OZON
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>

                        <Col xs={24} lg={12} style={{ display: 'flex' }}>
                            <Card
                                title="Настройки GPT OpenRouter"
                                className="rounded-lg shadow-lg"
                                style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                                styles={{
                                    body: {
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                }}
                            >
                                <Form
                                    form={openRouterForm}
                                    layout="vertical"
                                    onFinish={handleOpenRouterSubmit}
                                    requiredMark={false}
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                    initialValues={{
                                        server_url: 'https://openrouter.ai/api/v1'
                                    }}
                                >
                                    <Form.Item
                                        name="server_url"
                                        label="Адрес сервера"
                                        rules={[
                                            { required: true, message: 'Введите адрес сервера' },
                                            { type: 'url', message: 'Введите корректный URL' }
                                        ]}
                                    >
                                        <Input placeholder="https://openrouter.ai/api/v1" />
                                    </Form.Item>

                                    <Form.Item
                                        name="api_key"
                                        label="API Ключ"
                                        rules={[{ required: true, message: 'Введите API ключ' }]}
                                    >
                                        <Input.Password placeholder="API ключ OpenRouter" />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0, marginTop: 'auto' }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={openRouterLoading}
                                            block
                                        >
                                            Сохранить OpenRouter
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>

                        <Col xs={24} lg={12} style={{ display: 'flex' }}>
                            <Card
                                title="Настройки GPT NanoGPT"
                                className="rounded-lg shadow-lg"
                                style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
                                styles={{
                                    body: {
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                }}
                            >
                                <Form
                                    form={nanoGptForm}
                                    layout="vertical"
                                    onFinish={handleNanoGptSubmit}
                                    requiredMark={false}
                                    style={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                    initialValues={{
                                        server_url: 'https://nano-gpt.com/api/v1',
                                    }}
                                >
                                    <Form.Item
                                        name="server_url"
                                        label="Адрес сервера"
                                        rules={[
                                            { required: true, message: 'Введите адрес сервера' },
                                            { type: 'url', message: 'Введите корректный URL' },
                                        ]}
                                    >
                                        <Input placeholder="https://nano-gpt.com/api/v1" />
                                    </Form.Item>

                                    <Form.Item
                                        name="api_key"
                                        label="API Ключ"
                                        rules={[{ required: true, message: 'Введите API ключ' }]}
                                    >
                                        <Input.Password placeholder="API ключ NanoGPT" />
                                    </Form.Item>

                                    <Form.Item style={{ marginBottom: 0, marginTop: 'auto' }}>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={nanoGptLoading}
                                            block
                                        >
                                            Сохранить NanoGPT
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>
                    </Row>
                </div>

                <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Типы товаров
                    </h3>
                    <Card
                        title="Настройки типов товаров"
                        className="rounded-lg shadow-lg"
                    >
                        <p className="mb-4 text-sm text-gray-500">
                            Сначала заведите типы (Баффы, Хобы, полотенца), затем привяжите к ним
                            фрагмент артикула и стили. Порядок правил меняется перетаскиванием.
                        </p>

                        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
                            <div className="mb-2 font-medium text-gray-700">Справочник типов</div>
                            <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
                                <Input
                                    placeholder="Например: Баффы"
                                    value={newTypeName}
                                    onChange={(event) => setNewTypeName(event.target.value)}
                                    onPressEnter={addProductType}
                                />
                                <Button type="primary" icon={<PlusOutlined />} onClick={addProductType}>
                                    Добавить
                                </Button>
                            </Space.Compact>
                            <div className="mt-3 space-y-2">
                                {productTypes.length === 0 && (
                                    <div className="text-sm text-gray-400">Типы ещё не добавлены</div>
                                )}
                                {productTypes.map((typeName, index) => (
                                    <div
                                        key={`${typeName}-${index}`}
                                        className="flex items-center gap-2 rounded-md border border-gray-100 bg-gray-50 px-2 py-1.5"
                                    >
                                        {editingTypeIndex === index ? (
                                            <>
                                                <Input
                                                    size="small"
                                                    value={editingTypeValue}
                                                    onChange={(event) =>
                                                        setEditingTypeValue(event.target.value)
                                                    }
                                                    onPressEnter={saveEditProductType}
                                                    style={{ maxWidth: 260 }}
                                                />
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<CheckOutlined />}
                                                    onClick={saveEditProductType}
                                                />
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<CloseOutlined />}
                                                    onClick={cancelEditProductType}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <span className="min-w-0 flex-1 truncate">{typeName}</span>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => startEditProductType(index)}
                                                    aria-label="Редактировать тип"
                                                />
                                                <Button
                                                    danger
                                                    type="text"
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeProductType(index)}
                                                    aria-label="Удалить тип"
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Divider className="my-4">Правила отображения</Divider>

                        <Form
                            form={displayStylesForm}
                            layout="vertical"
                            onFinish={handleDisplayStylesSubmit}
                            initialValues={{ rules: [] }}
                            requiredMark={false}
                        >
                            <Form.List name="rules">
                                {(fields, { add, remove, move }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <div
                                                key={field.key}
                                                onDragOver={(event) => {
                                                    event.preventDefault();
                                                    event.dataTransfer.dropEffect = 'move';
                                                    if (dragOverIndex !== index) {
                                                        setDragOverIndex(index);
                                                    }
                                                }}
                                                onDragLeave={() => {
                                                    if (dragOverIndex === index) {
                                                        setDragOverIndex(null);
                                                    }
                                                }}
                                                onDrop={(event) => {
                                                    event.preventDefault();
                                                    const from = dragFromIndex;
                                                    if (from !== null && from !== index) {
                                                        move(from, index);
                                                    }
                                                    setDragFromIndex(null);
                                                    setDragOverIndex(null);
                                                }}
                                                className={`mb-3 rounded-lg border bg-gray-50/80 p-3 transition-shadow ${
                                                    dragOverIndex === index
                                                        ? 'border-blue-400 shadow-md'
                                                        : 'border-gray-200'
                                                } ${
                                                    dragFromIndex === index ? 'opacity-60' : ''
                                                }`}
                                            >
                                                <Row gutter={[12, 8]} align="bottom">
                                                    <Col flex="32px">
                                                        <Form.Item label=" " className="mb-0">
                                                            <div
                                                                draggable
                                                                onDragStart={(event) => {
                                                                    setDragFromIndex(index);
                                                                    event.dataTransfer.effectAllowed = 'move';
                                                                    event.dataTransfer.setData(
                                                                        'text/plain',
                                                                        String(index)
                                                                    );
                                                                }}
                                                                onDragEnd={() => {
                                                                    setDragFromIndex(null);
                                                                    setDragOverIndex(null);
                                                                }}
                                                                className="flex h-8 cursor-grab items-center justify-center text-gray-400 active:cursor-grabbing"
                                                                title="Перетащить"
                                                                aria-label="Перетащить"
                                                            >
                                                                <HolderOutlined style={{ fontSize: 16 }} />
                                                            </div>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={10} md={5}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'label']}
                                                            label="Тип"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'Выберите тип',
                                                                },
                                                            ]}
                                                            className="mb-0"
                                                        >
                                                            <Select
                                                                placeholder="Выберите тип"
                                                                options={typeSelectOptions}
                                                                showSearch
                                                                optionFilterProp="label"
                                                                notFoundContent={
                                                                    productTypes.length
                                                                        ? 'Ничего не найдено'
                                                                        : 'Сначала добавьте типы выше'
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={10} md={5}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'article']}
                                                            label="Артикул"
                                                            rules={[
                                                                {
                                                                    required: true,
                                                                    message: 'Укажите артикул',
                                                                },
                                                            ]}
                                                            className="mb-0"
                                                        >
                                                            <Input placeholder="BN-" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8} sm={4} md={2}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'backgroundColor']}
                                                            label="Фон"
                                                            className="mb-0"
                                                        >
                                                            <Input
                                                                type="color"
                                                                style={{ width: '100%', height: 32, padding: 2 }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8} sm={4} md={2}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'textColor']}
                                                            label="Шрифт"
                                                            className="mb-0"
                                                        >
                                                            <Input
                                                                type="color"
                                                                style={{ width: '100%', height: 32, padding: 2 }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={8} md={4}>
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'fontStyle']}
                                                            label="Стиль"
                                                            className="mb-0"
                                                        >
                                                            <Select options={FONT_STYLE_OPTIONS} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={16} sm={10} md={4}>
                                                        <Form.Item shouldUpdate noStyle>
                                                            {() => {
                                                                const rule =
                                                                    displayStylesForm.getFieldValue([
                                                                        'rules',
                                                                        field.name,
                                                                    ]) || {};
                                                                return (
                                                                    <Form.Item label="Превью" className="mb-0">
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                maxWidth: '100%',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap',
                                                                                padding: '4px 10px',
                                                                                borderRadius: 6,
                                                                                backgroundColor:
                                                                                    rule.backgroundColor || '#ffffff',
                                                                                color: rule.textColor || '#000000',
                                                                                fontWeight:
                                                                                    rule.fontStyle === 'bold' ||
                                                                                    rule.fontStyle === 'bold-italic'
                                                                                        ? 700
                                                                                        : 400,
                                                                                fontStyle:
                                                                                    rule.fontStyle === 'italic' ||
                                                                                    rule.fontStyle === 'bold-italic'
                                                                                        ? 'italic'
                                                                                        : 'normal',
                                                                                border: '1px solid #d9d9d9',
                                                                            }}
                                                                        >
                                                                            {rule.label || rule.article || 'Пример'}
                                                                        </span>
                                                                    </Form.Item>
                                                                );
                                                            }}
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={8} sm={4} md={2}>
                                                        <Form.Item label=" " className="mb-0">
                                                            <Button
                                                                danger
                                                                type="text"
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => remove(field.name)}
                                                                aria-label="Удалить правило"
                                                                style={{ height: 32 }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </div>
                                        ))}
                                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                            <Button
                                                type="dashed"
                                                onClick={() =>
                                                    add({
                                                        label: productTypes[0] || undefined,
                                                        article: '',
                                                        backgroundColor: '#fff7e6',
                                                        textColor: '#000000',
                                                        fontStyle: 'normal',
                                                    })
                                                }
                                                block
                                                icon={<PlusOutlined />}
                                            >
                                                Добавить правило отображения
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={displayStylesLoading}
                                                block
                                            >
                                                Сохранить типы товаров
                                            </Button>
                                        </Space>
                                    </>
                                )}
                            </Form.List>
                        </Form>
                    </Card>
                </div>

                <div>
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                        Названия товаров
                    </h3>
                    <Card
                        title="Сокращения названий"
                        className="rounded-lg shadow-lg"
                    >
                        <p className="mb-4 text-sm text-gray-500">
                            Длинные фразы в названиях заменяются в таблице заказов. Например,
                            «Шапка GoBananas бини летняя спортивная SUM» → «Шапка SUM».
                            Более длинные фразы применяются первыми. Полное название видно при наведении.
                        </p>
                        <Form
                            form={nameReplacementsForm}
                            layout="vertical"
                            onFinish={handleNameReplacementsSubmit}
                            initialValues={{ items: [{ find: '', replace: '' }] }}
                            requiredMark={false}
                        >
                            <Form.List name="items">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field) => (
                                            <Row
                                                key={field.key}
                                                gutter={[12, 8]}
                                                align="bottom"
                                                className="mb-3"
                                            >
                                                <Col xs={24} sm={11}>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, 'find']}
                                                        label="Фраза в названии"
                                                        className="mb-0"
                                                    >
                                                        <Input placeholder='Шапка GoBananas бини летняя спортивная SUM' />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={11}>
                                                    <Form.Item
                                                        {...field}
                                                        name={[field.name, 'replace']}
                                                        label="Заменить на"
                                                        className="mb-0"
                                                    >
                                                        <Input placeholder="Шапка SUM" />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={2}>
                                                    <Form.Item label=" " className="mb-0">
                                                        <Button
                                                            danger
                                                            type="text"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => remove(field.name)}
                                                            aria-label="Удалить сокращение"
                                                            style={{ height: 32 }}
                                                            disabled={fields.length === 1}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        ))}
                                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                                            <Button
                                                type="dashed"
                                                onClick={() => add({ find: '', replace: '' })}
                                                block
                                                icon={<PlusOutlined />}
                                            >
                                                Добавить сокращение
                                            </Button>
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                loading={nameReplacementsLoading}
                                                block
                                            >
                                                Сохранить сокращения
                                            </Button>
                                        </Space>
                                    </>
                                )}
                            </Form.List>
                        </Form>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Profile;
